import { ChangeEvent, FC, useState } from 'react';

import { Navigate } from 'react-router-dom';
import { Box, Button, debounce, Stack } from '@mui/material';

import { useGetAllOrganizations } from '../../../api-http/organizations';
import { AlertBar } from '../../../components/AlertBar';
import { useAuth } from '../../../context/auth-context';
import { UserRole } from '../../../types/user.types';
import { metaTitle } from '../../../util/tabTitle';
import { OrganizationsTable } from './OrganizationsTable';
import { AddOrganizationModal } from './components/AddOrganizationModal';
import { AddUserModal } from '../users/components/AddUserModal';
import { DEBOUNCE_TIME } from '../../../configs';
import Breadcrumbs from '../../../components/Breadcrumb';
import { useHandleSearch } from '../../../util/useHandleSearch';
import { useLocationSearch } from '../../../util/useLocationSearch';
import {
  managementControlSx,
  ManagementPageHeader,
  ManagementSearch,
  ManagementToolbar,
} from '../../../components/ManagementPageLayout';

const OrganizationPage: FC = () => {
  metaTitle('Super Admin - Organizations');

  const searchVal = useLocationSearch();
  const { currentUser } = useAuth();
  const { error, refetch } = useGetAllOrganizations();

  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(searchVal || '');
  const [openCreateOrgModal, setOpenCreateOrgModal] = useState(false);
  const [search, setSearch] = useState(searchVal || '');

  if (currentUser && currentUser.role !== UserRole.superAdmin) {
    return <Navigate to="/" />;
  }
  const selectedOrg = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSelectedOrgName(event.target.value);
  };
  useHandleSearch(search);

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[{ label: 'Organizations', to: '/admin/organizations' }]}
      />
      <ManagementPageHeader
        title="Organizations"
        subtitle="Manage organizations, packages, access, and platform status."
        actionLabel="New organization"
        onAction={() => setOpenCreateOrgModal(true)}
        secondaryAction={
          <Button
            variant="outlined"
            onClick={() => setOpenUserModal(true)}
            disabled={currentUser?.role === 'user'}
            sx={{ ...managementControlSx, fontWeight: 600 }}
          >
            Add user
          </Button>
        }
      />
      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search organizations"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            debounce(() => selectedOrg(event), DEBOUNCE_TIME)();
          }}
        />
        <Stack />
      </ManagementToolbar>

      <AlertBar severity="error" msg={error?.msg} />

      <OrganizationsTable selectedOrgName={selectedOrgName} />

      <AddOrganizationModal
        onClose={() => setOpenCreateOrgModal(false)}
        open={openCreateOrgModal}
        onFinished={() => refetch()}
      />

      <AddUserModal
        onClose={() => setOpenUserModal(false)}
        open={openUserModal}
        onFinished={() => refetch()}
      />
    </Box>
  );
};

export default OrganizationPage;
